/* eslint-disable max-len */
import React, { useState, useCallback, useRef, useContext, useMemo, useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Container,
  Row,
  Label,
  Progress,
} from 'reactstrap';

import { isBefore, isEqual, subDays, format } from 'date-fns';

import DatePickerMaskedInput from '../../components/DatePickerMaskedInput';
import Notification from '../../components/Notification';
import { AppContext } from '../../contexts/app';
import api from '../../services/api';

const months = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Fev' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Abr' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Ago' },
  { value: 9, label: 'Set' },
  { value: 10, label: 'Out' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dez' },
];

const STATUS_TRANSLATIONS = {
  idle: 'Aguardando',
  running: 'Em execução',
  completed: 'Concluído',
  error: 'Erro',
};

const STEP_TRANSLATIONS = {
  initializing: 'Inicializando',
  'loading-profissionais': 'Carregando profissionais',
  'loading-clientes': 'Carregando clientes',
  'processing-profissionais': 'Processando profissionais',
  'processing-clientes': 'Processando clientes',
  processing: 'Processando dados',
  'querying-financeiro': 'Consultando dados financeiros',
  'aggregating-data': 'Agregando dados',
  'formatting-csv': 'Formatando CSV',
  'generating-file': 'Gerando arquivo',
};

const STORAGE_KEY = 'horasTrabalhadasReportId';
const CLIENT_STORAGE_KEY = 'clientHorasTrabalhadasReportId';

function Relatorios() {
  const notificationRef = useRef();
  const { setLoading } = useContext(AppContext);
  const [financeiroStartDate, setFinanceiroStartDate] = useState(new Date());
  const [financeiroEndDate, setFinanceiroEndDate] = useState(new Date());
  const [comprovantesStartDate, setComprovantesStartDate] = useState(new Date());
  const [comprovantesEndDate, setComprovantesEndDate] = useState(new Date());
  const [userStartDate, setUserStartDate] = useState('');
  const [userEndDate, setUserEndDate] = useState('');
  const [professionalStartDate, setProfessionalStartDate] = useState('');
  const [professionalEndDate, setProfessionalEndDate] = useState('');
  const [horasTrabalhadasStartDate, setHorasTrabalhadasStartDate] = useState('');
  const [horasTrabalhadasEndDate, setHorasTrabalhadasEndDate] = useState('');
  const [reportProgress, setReportProgress] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const pollingIntervalRef = useRef(null);
  const [clientReportProgress, setClientReportProgress] = useState(null);
  const [isGeneratingClientReport, setIsGeneratingClientReport] = useState(false);
  const clientPollingIntervalRef = useRef(null);
  const [clientHorasTrabalhadasStartDate, setClientHorasTrabalhadasStartDate] = useState('');
  const [clientHorasTrabalhadasEndDate, setClientHorasTrabalhadasEndDate] = useState('');
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const [contasPagarMesAno, setContasPagarMesAno] = useState(`${currentMonth}-${currentYear}`);
  const [contasReceberMesAno, setContasReceberMesAno] = useState(`${currentMonth}-${currentYear}`);

  const mesAnoFilter = useMemo(
    () => {
      const filterCurrentYear = new Date().getFullYear();
      const nextYear = filterCurrentYear + 1;
      const previousYear = filterCurrentYear - 1;

      const filters = [];

      // Adiciona meses do ano anterior
      months.forEach((month) => {
        filters.push({
          value: `${month.value}-${previousYear}`,
          label: `${month.label}/${previousYear}`,
        });
      });

      // Adiciona meses do ano atual
      months.forEach((month) => {
        filters.push({
          value: `${month.value}-${filterCurrentYear}`,
          label: `${month.label}/${filterCurrentYear}`,
        });
      });

      // Adiciona meses do próximo ano
      months.forEach((month) => {
        filters.push({
          value: `${month.value}-${nextYear}`,
          label: `${month.label}/${nextYear}`,
        });
      });

      return filters;
    },
    [],
  );

  const handleFinanceiroGenerateReportCsv = useCallback(() => {
    if (!financeiroStartDate && !financeiroEndDate) {
      notificationRef.current.notify({
        message: 'Nenhuma data informada para gerar relatório',
        color: 'warning',
      });
      return;
    }

    if (!isEqual(financeiroStartDate, financeiroEndDate) && isBefore(financeiroEndDate, financeiroStartDate)) {
      notificationRef.current.notify({
        message: 'Data de início do período não pode ser maior que a data de fim do período',
        color: 'warning',
      });
      return;
    }

    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Aguarde... Estamos gerando o arquivo CSV... ',
        });

        // await fetch api
        const res = await api.get(`/financeiroCSVCreatorReports?startDate=${financeiroStartDate}&endDate=${financeiroEndDate}`, { responseType: 'blob' });
        const fileName = res.headers['content-disposition']
          .replace('attachment; filename=', '')
          .replace('"', '');

        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = fileName;
        alink.click();

        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error(err);

        if (err && err.response && err.response.data) {
          const { message } = err.response.data;

          if (message) {
            notificationRef.current.notify({
              message,
              color: 'danger',
            });

            return;
          }
        }

        if (err && err.response && err.response.status === 404) {
          notificationRef.current.notify({
            message: 'Nenhum registro encontrado para gerar o relatório',
            color: 'danger',
          });

          return;
        }

        notificationRef.current.notify({
          message: 'Não foi possível gerar o relatório com esse filtro. Tente períodos menores.',
          color: 'danger',
        });
      }
    })();
  }, [setLoading, financeiroStartDate, financeiroEndDate]);

  const handleComprovantesGenerateZip = useCallback(() => {
    if (!comprovantesStartDate && !comprovantesEndDate) {
      notificationRef.current.notify({
        message: 'Nenhuma data informada para gerar relatório',
        color: 'warning',
      });
      return;
    }

    if (!comprovantesStartDate || !comprovantesEndDate) {
      notificationRef.current.notify({
        message: 'Informe a data inicial e a data final do período',
        color: 'warning',
      });
      return;
    }

    if (!isEqual(comprovantesStartDate, comprovantesEndDate) && isBefore(comprovantesEndDate, comprovantesStartDate)) {
      notificationRef.current.notify({
        message: 'Data de início do período não pode ser maior que a data de fim do período',
        color: 'warning',
      });
      return;
    }

    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Aguarde... Estamos gerando o arquivo ZIP dos comprovantes...',
        });

        const startDateParam = format(comprovantesStartDate, 'yyyy-MM-dd');
        const endDateParam = format(comprovantesEndDate, 'yyyy-MM-dd');
        const fallbackFileName = `Comprovantes_${format(comprovantesStartDate, 'dd-MM-yyyy')}_${format(comprovantesEndDate, 'dd-MM-yyyy')}.zip`;

        const res = await api.get(
          `/financeiroProfissionalReembolsoComprovantesZip?startDate=${startDateParam}&endDate=${endDateParam}`,
          { responseType: 'blob' },
        );

        const contentDisposition = res.headers['content-disposition'] || '';
        const fileName = contentDisposition
          ? contentDisposition
            .replace('attachment; filename=', '')
            .replace(/"/g, '')
          : fallbackFileName;

        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = fileName || fallbackFileName;
        alink.click();
        window.URL.revokeObjectURL(fileURL);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error(err);

        if (err && err.response && err.response.data) {
          if (err.response.data instanceof Blob) {
            try {
              const errorText = await err.response.data.text();
              const errorJson = JSON.parse(errorText);
              if (errorJson && errorJson.message) {
                notificationRef.current.notify({
                  message: errorJson.message,
                  color: 'danger',
                });
                return;
              }
            } catch (parseError) {
              // segue para os fallbacks abaixo
            }
          } else if (err.response.data.message) {
            notificationRef.current.notify({
              message: err.response.data.message,
              color: 'danger',
            });
            return;
          }
        }

        if (err && err.response && err.response.status === 404) {
          notificationRef.current.notify({
            message: 'Nenhum comprovante encontrado para o período informado',
            color: 'danger',
          });
          return;
        }

        notificationRef.current.notify({
          message: 'Não foi possível gerar o ZIP com esse filtro. Tente períodos menores.',
          color: 'danger',
        });
      }
    })();
  }, [setLoading, comprovantesStartDate, comprovantesEndDate]);

  const handleUserGenerateReportCsv = useCallback(() => {
    if (userStartDate && userEndDate) {
      if (!isEqual(userStartDate, userEndDate) && isBefore(userEndDate, userStartDate)) {
        notificationRef.current.notify({
          message: 'Data de início do período não pode ser maior que a data de fim do período',
          color: 'warning',
        });
        return;
      }
    }

    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Aguarde... Estamos gerando o arquivo CSV... ',
        });

        // await fetch api
        const res = await api.get(`/userCSVCreatorReports?startDate=${userStartDate}&endDate=${userEndDate}`, { responseType: 'blob' });
        const fileName = res.headers['content-disposition']
          .replace('attachment; filename=', '')
          .replace('"', '');

        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = fileName;
        alink.click();

        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error(err);

        if (err && err.response && err.response.data) {
          const { message } = err.response.data;

          if (message) {
            notificationRef.current.notify({
              message,
              color: 'danger',
            });

            return;
          }
        }

        if (err && err.response && err.response.status === 404) {
          notificationRef.current.notify({
            message: 'Nenhum cliente encontrado com os dados informados, para gerar o relatório',
            color: 'danger',
          });

          return;
        }

        notificationRef.current.notify({
          message: 'Falha ao realizar ao gerar relatório',
          color: 'danger',
        });
      }
    })();
  }, [notificationRef, setLoading, userStartDate, userEndDate]);

  const handleProfessionalGenerateReportCsv = useCallback(() => {
    if (professionalStartDate && professionalEndDate) {
      if (!isEqual(professionalStartDate, professionalEndDate) && isBefore(professionalEndDate, professionalStartDate)) {
        notificationRef.current.notify({
          message: 'Data de início do período não pode ser maior que a data de fim do período',
          color: 'warning',
        });
        return;
      }
    }

    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Aguarde... Estamos gerando o arquivo CSV... ',
        });

        // await fetch api
        const res = await api.get(`/profissionalCSVCreatorReportLists?startDate=${professionalStartDate}&endDate=${professionalEndDate}`, { responseType: 'blob' });
        const fileName = res.headers['content-disposition']
          .replace('attachment; filename=', '')
          .replace('"', '');

        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = fileName;
        alink.click();

        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error(err);

        if (err && err.response && err.response.data) {
          const { message } = err.response.data;

          if (message) {
            notificationRef.current.notify({
              message,
              color: 'danger',
            });

            return;
          }
        }

        if (err && err.response && err.response.status === 404) {
          notificationRef.current.notify({
            message: 'Nenhum profissional encontrado com os dados informados, para gerar o relatório',
            color: 'danger',
          });

          return;
        }

        notificationRef.current.notify({
          message: 'Falha ao realizar ao gerar relatório',
          color: 'danger',
        });
      }
    })();
  }, [notificationRef, setLoading, professionalStartDate, professionalEndDate]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const downloadReportFile = useCallback(async (filename) => {
    try {
      setLoading({
        loading: true,
        message: 'Baixando arquivo...',
      });

      const res = await api.get(`/download/${filename}/files`, { responseType: 'blob' });
      const fileURL = window.URL.createObjectURL(res.data);
      const alink = document.createElement('a');
      alink.href = fileURL;
      alink.target = '_blank';
      alink.rel = 'noopener noreferrer';
      alink.download = filename;
      alink.click();

      setLoading(false);
      notificationRef.current.notify({
        message: 'Relatório baixado com sucesso!',
        color: 'success',
      });
    } catch (error) {
      setLoading(false);
      console.error(error);
      notificationRef.current.notify({
        message: 'Erro ao baixar o arquivo',
        color: 'danger',
      });
    }
  }, [setLoading]);

  const checkReportProgress = useCallback(async (reportId) => {
    try {
      const { data } = await api.get(`/profissionalReportProgress/${reportId}`);

      if (data && data.data) {
        setReportProgress(data.data);

        if (data.data.status === 'completed') {
          stopPolling();
          localStorage.removeItem(STORAGE_KEY);
          setIsGeneratingReport(false);

          if (data.data.fileInfo && data.data.fileInfo.filename) {
            await downloadReportFile(data.data.fileInfo.filename);
          }
        } else if (data.data.status === 'error') {
          stopPolling();
          localStorage.removeItem(STORAGE_KEY);
          setIsGeneratingReport(false);
          notificationRef.current.notify({
            message: data.data.errorMessage || 'Erro ao gerar o relatório',
            color: 'danger',
          });
        }
      }
    } catch (err) {
      console.error('Erro ao verificar progresso:', err);
      if (err && err.response && err.response.status === 404) {
        stopPolling();
        localStorage.removeItem(STORAGE_KEY);
        setIsGeneratingReport(false);
        setReportProgress(null);
        notificationRef.current.notify({
          message: 'Relatório não encontrado',
          color: 'warning',
        });
      }
    }
  }, [stopPolling, downloadReportFile]);

  const startPolling = useCallback((reportId) => {
    stopPolling();
    checkReportProgress(reportId);
    pollingIntervalRef.current = setInterval(() => {
      checkReportProgress(reportId);
    }, 5000);
  }, [checkReportProgress, stopPolling]);

  const handleHorasTrabalhadasGenerateReportCsv = useCallback(() => {
    if (horasTrabalhadasStartDate && horasTrabalhadasEndDate) {
      if (!isEqual(horasTrabalhadasStartDate, horasTrabalhadasEndDate) && isBefore(horasTrabalhadasEndDate, horasTrabalhadasStartDate)) {
        notificationRef.current.notify({
          message: 'Data de início do período não pode ser maior que a data de fim do período',
          color: 'warning',
        });
        return;
      }
    }

    (async () => {
      try {
        setIsGeneratingReport(true);
        setReportProgress(null);

        const startDateFormatted = horasTrabalhadasStartDate
          ? format(new Date(horasTrabalhadasStartDate), 'yyyy-MM-dd')
          : '';
        const endDateFormatted = horasTrabalhadasEndDate
          ? format(new Date(horasTrabalhadasEndDate), 'yyyy-MM-dd')
          : '';

        const res = await api.get(`/profissionalTiposAtendimentosMensalCSVCreatorReport?startDate=${startDateFormatted}&endDate=${endDateFormatted}`);

        if (res.data && res.data.reportId) {
          const { reportId } = res.data;
          localStorage.setItem(STORAGE_KEY, reportId);
          startPolling(reportId);
        } else {
          setIsGeneratingReport(false);
          notificationRef.current.notify({
            message: 'Erro ao iniciar geração do relatório',
            color: 'danger',
          });
        }
      } catch (err) {
        setIsGeneratingReport(false);
        console.error(err);

        if (err && err.response && err.response.data) {
          const { message } = err.response.data;

          if (message) {
            notificationRef.current.notify({
              message,
              color: 'danger',
            });

            return;
          }
        }

        notificationRef.current.notify({
          message: 'Falha ao iniciar geração do relatório',
          color: 'danger',
        });
      }
    })();
  }, [horasTrabalhadasStartDate, horasTrabalhadasEndDate, startPolling]);

  useEffect(() => {
    const savedReportId = localStorage.getItem(STORAGE_KEY);
    if (savedReportId) {
      setIsGeneratingReport(true);
      startPolling(savedReportId);
    }

    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  const stopClientPolling = useCallback(() => {
    if (clientPollingIntervalRef.current) {
      clearInterval(clientPollingIntervalRef.current);
      clientPollingIntervalRef.current = null;
    }
  }, []);

  const downloadClientReportFile = useCallback(async (filename) => {
    try {
      setLoading({
        loading: true,
        message: 'Baixando arquivo...',
      });

      const res = await api.get(`/download/${filename}/files`, { responseType: 'blob' });
      const fileURL = window.URL.createObjectURL(res.data);
      const alink = document.createElement('a');
      alink.href = fileURL;
      alink.target = '_blank';
      alink.rel = 'noopener noreferrer';
      alink.download = filename;
      alink.click();

      setLoading(false);
      notificationRef.current.notify({
        message: 'Relatório baixado com sucesso!',
        color: 'success',
      });
    } catch (error) {
      setLoading(false);
      console.error(error);
      notificationRef.current.notify({
        message: 'Erro ao baixar o arquivo',
        color: 'danger',
      });
    }
  }, [setLoading]);

  const checkClientReportProgress = useCallback(async (reportId) => {
    try {
      const { data } = await api.get(`/userReportProgress/${reportId}`);

      if (data && data.data) {
        setClientReportProgress(data.data);

        if (data.data.status === 'completed') {
          stopClientPolling();
          localStorage.removeItem(CLIENT_STORAGE_KEY);
          setIsGeneratingClientReport(false);

          if (data.data.fileInfo && data.data.fileInfo.filename) {
            await downloadClientReportFile(data.data.fileInfo.filename);
          }
        } else if (data.data.status === 'error') {
          stopClientPolling();
          localStorage.removeItem(CLIENT_STORAGE_KEY);
          setIsGeneratingClientReport(false);
          notificationRef.current.notify({
            message: data.data.errorMessage || 'Erro ao gerar o relatório',
            color: 'danger',
          });
        }
      }
    } catch (err) {
      console.error('Erro ao verificar progresso:', err);
      if (err && err.response && err.response.status === 404) {
        stopClientPolling();
        localStorage.removeItem(CLIENT_STORAGE_KEY);
        setIsGeneratingClientReport(false);
        setClientReportProgress(null);
        notificationRef.current.notify({
          message: 'Relatório não encontrado',
          color: 'warning',
        });
      }
    }
  }, [stopClientPolling, downloadClientReportFile]);

  const startClientPolling = useCallback((reportId) => {
    stopClientPolling();
    checkClientReportProgress(reportId);
    clientPollingIntervalRef.current = setInterval(() => {
      checkClientReportProgress(reportId);
    }, 5000);
  }, [checkClientReportProgress, stopClientPolling]);

  const handleClientHorasTrabalhadasGenerateReportCsv = useCallback(() => {
    if (clientHorasTrabalhadasStartDate && clientHorasTrabalhadasEndDate) {
      if (!isEqual(clientHorasTrabalhadasStartDate, clientHorasTrabalhadasEndDate) && isBefore(clientHorasTrabalhadasEndDate, clientHorasTrabalhadasStartDate)) {
        notificationRef.current.notify({
          message: 'Data de início do período não pode ser maior que a data de fim do período',
          color: 'warning',
        });
        return;
      }
    }

    (async () => {
      try {
        setIsGeneratingClientReport(true);
        setClientReportProgress(null);

        const startDateFormatted = clientHorasTrabalhadasStartDate
          ? format(new Date(clientHorasTrabalhadasStartDate), 'yyyy-MM-dd')
          : '';
        const endDateFormatted = clientHorasTrabalhadasEndDate
          ? format(new Date(clientHorasTrabalhadasEndDate), 'yyyy-MM-dd')
          : '';

        const res = await api.get(`/userTiposAtendimentosMensalCSVCreatorReport?startDate=${startDateFormatted}&endDate=${endDateFormatted}`);

        if (res.data && res.data.reportId) {
          const { reportId } = res.data;
          localStorage.setItem(CLIENT_STORAGE_KEY, reportId);
          startClientPolling(reportId);
        } else {
          setIsGeneratingClientReport(false);
          notificationRef.current.notify({
            message: 'Erro ao iniciar geração do relatório',
            color: 'danger',
          });
        }
      } catch (err) {
        setIsGeneratingClientReport(false);
        console.error(err);

        if (err && err.response && err.response.data) {
          const { message } = err.response.data;

          if (message) {
            notificationRef.current.notify({
              message,
              color: 'danger',
            });

            return;
          }
        }

        notificationRef.current.notify({
          message: 'Falha ao iniciar geração do relatório',
          color: 'danger',
        });
      }
    })();
  }, [clientHorasTrabalhadasStartDate, clientHorasTrabalhadasEndDate, startClientPolling]);

  useEffect(() => {
    const savedClientReportId = localStorage.getItem(CLIENT_STORAGE_KEY);
    if (savedClientReportId) {
      setIsGeneratingClientReport(true);
      startClientPolling(savedClientReportId);
    }

    return () => {
      stopClientPolling();
    };
  }, [startClientPolling, stopClientPolling]);

  const handleContasPagarGenerateReport = useCallback(() => {
    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Aguarde... Estamos gerando o arquivo Excel... ',
        });

        const [mes, ano] = contasPagarMesAno.split('-');

        // await fetch api
        const res = await api.get(`/financeiroContaPagarExcelCreatorReportDemonstrativoTotalizados?mes=${mes}&ano=${ano}`, { responseType: 'blob' });
        let fileName = res.headers['content-disposition']
          .replace('attachment; filename=', '')
          .replace(/"/g, '')
          .trim();

        // Remove underscore do final se existir
        if (fileName.endsWith('_')) {
          fileName = fileName.slice(0, -1);
        }

        console.log('🚀 ~ fileName:', fileName);

        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = fileName;
        alink.click();

        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error(err);

        if (err && err.response && err.response.data) {
          const { message } = err.response.data;

          if (message) {
            notificationRef.current.notify({
              message,
              color: 'danger',
            });

            return;
          }
        }

        notificationRef.current.notify({
          message: 'Falha ao gerar relatório de contas a pagar',
          color: 'danger',
        });
      }
    })();
  }, [setLoading, contasPagarMesAno]);

  const handleContasReceberGenerateReport = useCallback(() => {
    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Aguarde... Estamos gerando o arquivo Excel... ',
        });

        const [mes, ano] = contasReceberMesAno.split('-');

        // await fetch api
        const res = await api.get(`/financeiroContaReceberExcelCreatorReportDemonstrativoTotalizados?mes=${mes}&ano=${ano}`, { responseType: 'blob' });
        let fileName = res.headers['content-disposition']
          .replace('attachment; filename=', '')
          .replace(/"/g, '')
          .trim();

        // Remove underscore do final se existir
        if (fileName.endsWith('_')) {
          fileName = fileName.slice(0, -1);
        }

        console.log('🚀 ~ fileName:', fileName);

        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = fileName;
        alink.click();

        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error(err);

        if (err && err.response && err.response.data) {
          const { message } = err.response.data;

          if (message) {
            notificationRef.current.notify({
              message,
              color: 'danger',
            });

            return;
          }
        }

        notificationRef.current.notify({
          message: 'Falha ao gerar relatório de contas a receber',
          color: 'danger',
        });
      }
    })();
  }, [setLoading, contasReceberMesAno]);

  const handleTiposAtendimentosGenerateReportCsv = useCallback(() => {
    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Aguarde... Estamos gerando o arquivo CSV... ',
        });

        // await fetch api
        const res = await api.get('/profissionalTiposAtendimentosCSVCreatorReport', { responseType: 'blob' });
        const fileName = res.headers['content-disposition']
          .replace('attachment; filename=', '')
          .replace('"', '');

        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = fileName;
        alink.click();

        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error(err);

        if (err && err.response && err.response.data) {
          const { message } = err.response.data;

          if (message) {
            notificationRef.current.notify({
              message,
              color: 'danger',
            });

            return;
          }
        }

        if (err && err.response && err.response.status === 404) {
          notificationRef.current.notify({
            message: 'Nenhum registro encontrado para gerar o relatório',
            color: 'danger',
          });

          return;
        }

        notificationRef.current.notify({
          message: 'Não foi possível gerar o relatório com esse filtro. Tente períodos menores.',
          color: 'danger',
        });
      }
    })();
  }, [setLoading]);

  return (
    <Container fluid>
      <Row className="mt-5">
        <Col>
          <Notification ref={notificationRef} />
          <Card>
            <CardBody>
              <Row>
                <Col>
                  <h3>Relatórios</h3>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <CardHeader>
              <h5>Financeiro Consolidado GERAL</h5>
            </CardHeader>
            <CardBody>
              <Row className="mb-2">
                <Col>
                  <Button
                    color="primary"
                    onClick={() => {
                      setFinanceiroStartDate(subDays(new Date(), 15));
                      setFinanceiroEndDate(new Date());
                    }}
                  >
                    Últimos 15 dias
                  </Button>
                  <Button
                    className="ml-2"
                    color="primary"
                    onClick={() => {
                      setFinanceiroStartDate(subDays(new Date(), 30));
                      setFinanceiroEndDate(new Date());
                    }}
                  >
                    Últimos 30 dias
                  </Button>
                  <Button
                    className="ml-2"
                    color="primary"
                    onClick={() => {
                      setFinanceiroStartDate(subDays(new Date(), 60));
                      setFinanceiroEndDate(new Date());
                    }}
                  >
                    Últimos 60 dias
                  </Button>
                  <Button
                    className="ml-2"
                    color="primary"
                    onClick={() => {
                      setFinanceiroStartDate(subDays(new Date(), 90));
                      setFinanceiroEndDate(new Date());
                    }}
                  >
                    Últimos 90 dias
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Início do Período"
                    placeholderText="Início do Período"
                    selected={financeiroStartDate}
                    onChange={(date) => setFinanceiroStartDate(date)}
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Fim do Período"
                    placeholderText="Fim do Período"
                    selected={financeiroEndDate}
                    onChange={(date) => setFinanceiroEndDate(date)}
                  />
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleFinanceiroGenerateReportCsv}
                  >
                    Gerar Relatório
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col>
          <Card>
            <CardHeader>
              <h5>Exportação de Comprovantes</h5>
            </CardHeader>
            <CardBody>
              <Row className="mb-2">
                <Col>
                  <Button
                    color="primary"
                    onClick={() => {
                      setComprovantesStartDate(subDays(new Date(), 15));
                      setComprovantesEndDate(new Date());
                    }}
                  >
                    Últimos 15 dias
                  </Button>
                  <Button
                    className="ml-2"
                    color="primary"
                    onClick={() => {
                      setComprovantesStartDate(subDays(new Date(), 30));
                      setComprovantesEndDate(new Date());
                    }}
                  >
                    Últimos 30 dias
                  </Button>
                  <Button
                    className="ml-2"
                    color="primary"
                    onClick={() => {
                      setComprovantesStartDate(subDays(new Date(), 60));
                      setComprovantesEndDate(new Date());
                    }}
                  >
                    Últimos 60 dias
                  </Button>
                  <Button
                    className="ml-2"
                    color="primary"
                    onClick={() => {
                      setComprovantesStartDate(subDays(new Date(), 90));
                      setComprovantesEndDate(new Date());
                    }}
                  >
                    Últimos 90 dias
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Data Inicial (Data da Compra)"
                    placeholderText="Data Inicial"
                    selected={comprovantesStartDate}
                    onChange={(date) => setComprovantesStartDate(date)}
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Data Final (Data da Compra)"
                    placeholderText="Data Final"
                    selected={comprovantesEndDate}
                    onChange={(date) => setComprovantesEndDate(date)}
                  />
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleComprovantesGenerateZip}
                  >
                    Gerar Relatório
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col>
          <Card>
            <CardHeader>
              <h5>DEMONSTRATIVO CONTAS A PAGAR TOTALIZADO</h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col xs={12} sm={6} lg={4}>
                  <FormGroup>
                    <Label for="contasPagarMesAno">Mês</Label>
                    <Input
                      type="select"
                      defaultValue={contasPagarMesAno || ''}
                      value={contasPagarMesAno}
                      onChange={({ target: { value } }) => {
                        setContasPagarMesAno(value);
                      }}
                    >
                      <option value="">Selecione</option>
                      {mesAnoFilter.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleContasPagarGenerateReport}
                  >
                    Gerar relatório
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col>
          <Card>
            <CardHeader>
              <h5>DEMONSTRATIVO CONTAS A RECEBER TOTALIZADO</h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col xs={12} sm={6} lg={4}>
                  <FormGroup>
                    <Label for="contasReceberMesAno">Mês</Label>
                    <Input
                      type="select"
                      defaultValue={contasReceberMesAno || ''}
                      value={contasReceberMesAno}
                      onChange={({ target: { value } }) => {
                        setContasReceberMesAno(value);
                      }}
                    >
                      <option value="">Selecione</option>
                      {mesAnoFilter.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleContasReceberGenerateReport}
                  >
                    Gerar relatório
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col>
          <Card>
            <CardHeader>
              <h5>Clientes</h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Data de Cadastro - Início"
                    placeholderText="Data de Cadastro - Início"
                    selected={userStartDate}
                    onChange={(date) => setUserStartDate(date)}
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Data de Cadastro - Fim"
                    placeholderText="Data de Cadastro - Fim"
                    selected={userEndDate}
                    onChange={(date) => setUserEndDate(date)}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  Obs.: Para gerar relatório de todos os clientes, deixe os campos de data de cadastro em branco.
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleUserGenerateReportCsv}
                  >
                    Gerar Relatório
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col>
          <Card>
            <CardHeader>
              <h5>Profissionais</h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Data de Cadastro - Início"
                    placeholderText="Data de Cadastro - Início"
                    selected={professionalStartDate}
                    onChange={(date) => setProfessionalStartDate(date)}
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Data de Cadastro - Fim"
                    placeholderText="Data de Cadastro - Fim"
                    selected={professionalEndDate}
                    onChange={(date) => setProfessionalEndDate(date)}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  Obs.: Para gerar relatório de todos os profissionais, deixe os campos de data de cadastro em branco.
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleProfessionalGenerateReportCsv}
                  >
                    Gerar Relatório
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col>
          <Card>
            <CardHeader>
              <h5>RELATÓRIO DE TIPOS DE ATENDIMENTOS POR PROFISSIONAL</h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col>
                  <p>Gere o relatório de tipos de atendimentos por profissional em formato CSV.</p>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleTiposAtendimentosGenerateReportCsv}
                  >
                    Gerar Relatório
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col>
          <Card>
            <CardHeader>
              <h5>RELATÓRIO TOTALIZADO DE HORAS TRABALHADAS POR PERÍODO E TIPO DE ATENDIMENTO - ESPECIALISTA</h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Data de Cadastro - Início"
                    placeholderText="Data de Cadastro - Início"
                    selected={horasTrabalhadasStartDate}
                    onChange={(date) => setHorasTrabalhadasStartDate(date)}
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Data de Cadastro - Fim"
                    placeholderText="Data de Cadastro - Fim"
                    selected={horasTrabalhadasEndDate}
                    onChange={(date) => setHorasTrabalhadasEndDate(date)}
                  />
                </Col>
              </Row>
              {isGeneratingReport && reportProgress && (
                <Row className="mt-4">
                  <Col>
                    <Card className="bg-light">
                      <CardBody>
                        <Row>
                          <Col>
                            <div className="d-flex mb-2">
                              <strong>Status:</strong>
                              <span className="ml-2">{STATUS_TRANSLATIONS[reportProgress.status] || reportProgress.status}</span>
                            </div>
                            <div className="d-flex mb-2">
                              <strong>Etapa:</strong>
                              <span className="ml-2">{STEP_TRANSLATIONS[reportProgress.currentStep] || reportProgress.currentStep}</span>
                            </div>
                            {reportProgress.currentMessage && (
                              <div className="mb-3">
                                <strong>Mensagem:</strong>
                                <p className="mb-0">{reportProgress.currentMessage}</p>
                              </div>
                            )}
                            {reportProgress.totalToProcess > 0 && (
                              <div className="d-flex justify-content-between mb-2">
                                <span>
                                  Processado:
                                  {' '}
                                  {reportProgress.processedCount}
                                  /
                                  {reportProgress.totalToProcess}
                                </span>
                                <span>
                                  {Math.round((reportProgress.processedCount / reportProgress.totalToProcess) * 100)}
                                  %
                                </span>
                              </div>
                            )}
                            <Progress
                              value={reportProgress.totalToProcess > 0
                                ? (reportProgress.processedCount / reportProgress.totalToProcess) * 100
                                : 0}
                              color={(() => {
                                if (reportProgress.status === 'completed') return 'success';
                                if (reportProgress.status === 'error') return 'danger';
                                return 'primary';
                              })()}
                              className="mb-3"
                            />
                            {reportProgress.details && (
                              <div className="small text-muted">
                                <div>
                                  Profissionais:
                                  {' '}
                                  {reportProgress.details.processedProfissionais}
                                  /
                                  {reportProgress.details.totalProfissionais}
                                </div>
                                <div>
                                  Especialidades:
                                  {' '}
                                  {reportProgress.details.processedEspecialidades}
                                  /
                                  {reportProgress.details.totalEspecialidades}
                                </div>
                                {reportProgress.details.totalRows > 0 && (
                                  <div>
                                    Total de linhas:
                                    {' '}
                                    {reportProgress.details.totalRows}
                                  </div>
                                )}
                              </div>
                            )}
                            {reportProgress.status === 'completed' && reportProgress.fileInfo && reportProgress.fileInfo.fileSize && (
                              <div className="mt-2 small text-success">
                                <i className="fas fa-check-circle mr-2" />
                                Arquivo gerado com sucesso! (
                                {(reportProgress.fileInfo.fileSize / 1024).toFixed(2)}
                                {' '}
                                KB)
                              </div>
                            )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            </CardBody>
            <CardFooter>
              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleHorasTrabalhadasGenerateReportCsv}
                    disabled={isGeneratingReport}
                  >
                    {isGeneratingReport ? 'Gerando Relatório...' : 'Gerar Relatório'}
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col>
          <Card>
            <CardHeader>
              <h5>RELATÓRIO TOTALIZADO DE HORAS TRABALHADAS POR PERÍODO E TIPO DE ATENDIMENTO - CLIENTE</h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Data de Cadastro - Início"
                    placeholderText="Data de Cadastro - Início"
                    selected={clientHorasTrabalhadasStartDate}
                    onChange={(date) => setClientHorasTrabalhadasStartDate(date)}
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <DatePickerMaskedInput
                    label="Data de Cadastro - Fim"
                    placeholderText="Data de Cadastro - Fim"
                    selected={clientHorasTrabalhadasEndDate}
                    onChange={(date) => setClientHorasTrabalhadasEndDate(date)}
                  />
                </Col>
              </Row>
              {isGeneratingClientReport && clientReportProgress && (
                <Row className="mt-4">
                  <Col>
                    <Card className="bg-light">
                      <CardBody>
                        <Row>
                          <Col>
                            <div className="d-flex mb-2">
                              <strong>Status:</strong>
                              <span className="ml-2">{STATUS_TRANSLATIONS[clientReportProgress.status] || clientReportProgress.status}</span>
                            </div>
                            <div className="d-flex mb-2">
                              <strong>Etapa:</strong>
                              <span className="ml-2">{STEP_TRANSLATIONS[clientReportProgress.currentStep] || clientReportProgress.currentStep}</span>
                            </div>
                            {clientReportProgress.currentMessage && (
                              <div className="mb-3">
                                <strong>Mensagem:</strong>
                                <p className="mb-0">{clientReportProgress.currentMessage}</p>
                              </div>
                            )}
                            {clientReportProgress.totalToProcess > 0 && (
                              <div className="d-flex justify-content-between mb-2">
                                <span>
                                  Processado:
                                  {' '}
                                  {clientReportProgress.processedCount}
                                  /
                                  {clientReportProgress.totalToProcess}
                                  {' '}
                                  (Clientes + Especialidades)
                                </span>
                                <span>
                                  {Math.round((clientReportProgress.processedCount / clientReportProgress.totalToProcess) * 100)}
                                  %
                                </span>
                              </div>
                            )}
                            <Progress
                              value={clientReportProgress.totalToProcess > 0
                                ? (clientReportProgress.processedCount / clientReportProgress.totalToProcess) * 100
                                : 0}
                              color={(() => {
                                if (clientReportProgress.status === 'completed') return 'success';
                                if (clientReportProgress.status === 'error') return 'danger';
                                return 'primary';
                              })()}
                              className="mb-3"
                            />
                            {clientReportProgress.details && (
                              <div className="small text-muted">
                                {/* <div>
                                  Profissionais:
                                  {' '}
                                  {clientReportProgress.details.processedProfissionais}
                                  /
                                  {clientReportProgress.details.totalProfissionais}
                                </div>
                                <div>
                                  Especialidades:
                                  {' '}
                                  {clientReportProgress.details.processedEspecialidades}
                                  /
                                  {clientReportProgress.details.totalEspecialidades}
                                </div> */}
                                {clientReportProgress.details.totalRows > 0 && (
                                  <div>
                                    Total de linhas:
                                    {' '}
                                    {clientReportProgress.details.totalRows}
                                  </div>
                                )}
                              </div>
                            )}
                            {clientReportProgress.status === 'completed' && clientReportProgress.fileInfo && clientReportProgress.fileInfo.fileSize && (
                              <div className="mt-2 small text-success">
                                <i className="fas fa-check-circle mr-2" />
                                Arquivo gerado com sucesso! (
                                {(clientReportProgress.fileInfo.fileSize / 1024).toFixed(2)}
                                {' '}
                                KB)
                              </div>
                            )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            </CardBody>
            <CardFooter>
              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleClientHorasTrabalhadasGenerateReportCsv}
                    disabled={isGeneratingClientReport}
                  >
                    {isGeneratingClientReport ? 'Gerando Relatório...' : 'Gerar Relatório'}
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Relatorios;
